import type { TokenCredential } from "@azure/identity";
import { isEmailFeatureUsable, type EmailFeatureStamp } from "@m8t-stack/platform-release";
import { getAgentVersion, type AgentDefinition } from "./foundry-agent-get.js";
import { createAgentVersion } from "./foundry-agent-version.js";
import { listM8tAgents } from "./foundry-discovery.js";

/** The three environment variables that carry outbound-email wiring to the executor.
 *
 *  `M8T_EMAIL_ENABLED` is separate from the two ACS values on purpose: it lets the
 *  executor tell "my operator turned this off" apart from "my ACS wiring is missing, which
 *  is a bug". Those two produced the same message before, which is why a six-week outage
 *  read like a design decision. */
export const EMAIL_ENV_KEYS = ["M8T_ACS_ENDPOINT", "M8T_ACS_SENDER", "M8T_EMAIL_ENABLED"] as const;

const FOUNDRY_HOSTED_HEADER = { "Foundry-Features": "HostedAgents=V1Preview" };

/** Return a copy of `def` with the email env set from `feature`, or stripped when email is
 *  off or unrecorded. Pure — never mutates its input, and never touches any other field of
 *  the definition (image, cpu, memory, tools, protocol versions all ride through).
 *
 *  An `enabled: true` feature with no endpoint or sender is treated as OFF: writing a blank
 *  sender would produce an executor that believes it can send and then fails at the wire. */
export function applyEmailEnv(def: AgentDefinition, feature: EmailFeatureStamp | null): AgentDefinition {
  const live = (def.environment_variables as Record<string, string> | undefined) ?? {};
  // Rebuild without the email keys rather than deleting them, so every other variable the
  // executor depends on is carried through verbatim.
  const carried: Record<string, string> = Object.fromEntries(
    Object.entries(live).filter(([k]) => !(EMAIL_ENV_KEYS as readonly string[]).includes(k)),
  );

  if (feature !== null && isEmailFeatureUsable(feature)) {
    return {
      ...def,
      environment_variables: {
        ...carried,
        M8T_ACS_ENDPOINT: feature.acsEndpoint,
        M8T_ACS_SENDER: feature.acsSender,
        M8T_EMAIL_ENABLED: "true",
      },
    };
  }
  return { ...def, environment_variables: carried };
}

export interface ReconcileResult {
  /** True when a new agent version was posted. */
  changed: boolean;
  /** True when no executor is deployed (a genuine 404), so there was nothing to reconcile. */
  absent: boolean;
  /** Set when the executor exists but could not be READ (auth, throttle, 5xx). Distinct from
   *  `absent` on purpose: reporting a 403 as "not deployed" is the same conflation of broken
   *  with off that this whole change exists to eliminate. */
  unreadable?: string;
  /** The new version id, when one was posted. */
  version?: string;
}

/**
 * Bring a deployed executor's email env into line with the recorded intent.
 *
 * This is the self-healing leg. `applyAgentImage` in the converge path swaps only the
 * `image` field, so a stamp flag on its own would never reach an already-deployed
 * executor — which is exactly the state the canary sat in: ACS alive and verified, stamp
 * silent, executor env empty.
 *
 * Idempotent by comparison, not by hope: when the live definition already matches, NO new
 * version is posted. That matters because converge ticks every five minutes, and an
 * unconditional POST would mint a fresh agent version on every tick forever.
 *
 * A missing executor is reported, not thrown — an install without one is a normal state
 * (no customer install deploys the executor yet), and callers decide whether to warn.
 */
export async function reconcileExecutorEmailEnv(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
  feature: EmailFeatureStamp | null;
  onProgress?: (m: string) => void;
}): Promise<ReconcileResult> {
  const { credential, projectEndpoint, agentName, feature, onProgress } = args;

  let current;
  try {
    current = await getAgentVersion({ credential, projectEndpoint, agentName });
  } catch (e) {
    // Only a genuine 404 means "not deployed". FOUNDRY_AUTH_FAILED / AGENT_GET_FAILED cover
    // 401, 403, 429 and 5xx — reporting any of those as absent would tell an operator their
    // executor does not exist when it does, and would let converge silently skip a real
    // reconcile. That is exactly the broken-reads-as-off failure this change exists to kill.
    const code = (e as { code?: unknown }).code;
    if (code === "AGENT_NOT_FOUND") return { changed: false, absent: true };
    return { changed: false, absent: false, unreadable: e instanceof Error ? e.message : String(e) };
  }

  const next = applyEmailEnv(current.definition, feature);
  const before = (current.definition.environment_variables as Record<string, string> | undefined) ?? {};
  const after = next.environment_variables as Record<string, string>;
  if (EMAIL_ENV_KEYS.every((k) => before[k] === after[k])) {
    return { changed: false, absent: false };
  }

  onProgress?.(`reconciling outbound-email env on ${agentName}…`);
  const version = await createAgentVersion({
    credential,
    projectEndpoint,
    agentName,
    definition: next,
    // Carry the live metadata through unchanged: it holds the a2a card and brain markers,
    // and dropping those has bitten this repo before.
    metadata: current.metadata ?? {},
    extraHeaders: FOUNDRY_HOSTED_HEADER,
  });
  return { changed: true, absent: false, version };
}

/** The persona the executor is deployed FROM. The deployed agent INSTANCE is normally
 *  `ezra-executor`, but the instance name is operator-chosen and was renamed once already —
 *  which is what caused this outage. Discovery keys off `metadata.persona`, which does not
 *  move. */
export const EXECUTOR_PERSONA = "azure-executor";

/** Default instance name, used only as a last resort when discovery finds nothing. */
export const EXECUTOR_AGENT_FALLBACK = "ezra-executor";

/** Where the name came from. THREE states, not a boolean: a boolean conflates "I guessed"
 *  with "you told me", and the two want opposite advice. Telling an operator who just passed
 *  `--agent my-exec` that `my-exec` is a fallback and they should try `--agent` sends them
 *  chasing the name they supplied. */
export type ExecutorNameProvenance = "discovered" | "override" | "fallback";

/**
 * Resolve the deployed executor's agent name by persona, falling back to the conventional
 * instance name.
 *
 * Hardcoding `ezra-executor` at each call site would break for any install whose executor is
 * named differently — the operator would get "no ezra-executor is deployed" while one plainly
 * was, and a flip would silently reconcile nothing. Rename-resilience is the whole point of
 * this change, so the lookup must not itself depend on a name.
 */
export async function resolveExecutorAgentName(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  /** An explicit override (`--agent`), which always wins. */
  override?: string;
}): Promise<{ agentName: string; provenance: ExecutorNameProvenance }> {
  if (typeof args.override === "string" && args.override.length > 0) {
    return { agentName: args.override, provenance: "override" };
  }
  try {
    const agents = await listM8tAgents({ credential: args.credential, projectEndpoint: args.projectEndpoint });
    const hit = agents.find((a) => a.metadata.persona === EXECUTOR_PERSONA);
    if (hit) return { agentName: hit.name, provenance: "discovered" };
  } catch {
    // Discovery is best-effort; the fallback keeps the command usable.
  }
  return { agentName: EXECUTOR_AGENT_FALLBACK, provenance: "fallback" };
}
